import type {
  DiffEntry,
  InvoiceVersion,
  Transaction,
} from 'payments-api/model';
import {
  useGetInvoicesIdHistorySuspense,
  useGetTransactions,
} from 'api/payments/generated/fragmentPaymentsAPI';
import clsx from 'clsx';
import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';

import type { CurrencyCode } from '@fragment/types/currencyCodes';
import { currencyPrecision as currencyPrecisionMap } from '@fragment/types/currencyCodes';

import { FormatBillingAmount } from './formatBillingAmount';
import { ItemColorSquare } from './ItemColorSquare';
import { RoleColorDot } from './RoleColorDot';

type HistoryRow = {
  key: string;
  date: string;
  timestamp: number;
  content: React.ReactNode;
};

type UserMap = Map<string, string>;

const formatDateLabel = (dateStr: string): string =>
  new Date(dateStr).toDateString().split(' ').slice(1).join(' ');

const buildDiffRows = (
  entry: DiffEntry,
  date: string,
  timestamp: number,
  key: string,
  userRoleMap: UserMap
): HistoryRow => {
  if ('old_amount' in entry) {
    return {
      key,
      date,
      timestamp,
      content: (
        <div className="text-main text-xmd">
          <span>Updated</span>
          <br />
          <span className="text-main-500">{entry.id.slice(0, 8)}</span>
          <br />
          <span className="text-main-500">
            {entry.old_amount} → {entry.new_amount}
          </span>
        </div>
      ),
    };
  }
  const isAdd = String(entry.op).includes('add');
  const precision =
    currencyPrecisionMap[entry.item.currency_code as CurrencyCode] || 2;
  const role = userRoleMap.get(entry.item.user_id);
  return {
    key,
    date,
    timestamp,
    content: (
      <div className="text-main text-xmd flex flex-col gap-y-[2px]">
        <span>{isAdd ? 'Added' : 'Removed'}</span>
        <div className="flex items-center gap-x-[6px]">
          <ItemColorSquare
            productId={entry.item.product_id}
            size={10}
            hoverAnimate
          />
          <span className="text-main-500">{entry.item.product_id}</span>
        </div>
        <div className="flex items-center gap-x-[6px]">
          {role ? (
            <RoleColorDot role={role} size={10} hoverAnimate />
          ) : (
            <ItemColorSquare productId={entry.item.user_id} size={10} />
          )}
          <span className="text-main-500">{entry.item.user_id}</span>
        </div>
        <FormatBillingAmount
          amount={Number(entry.item.amount)}
          currencyCode={entry.item.currency_code as CurrencyCode}
          currencyPrecision={precision}
          secondary
        />
      </div>
    ),
  };
};

const flattenVersions = (
  versions: InvoiceVersion[],
  userRoleMap: UserMap
): HistoryRow[] => {
  const rows: HistoryRow[] = [];
  versions.forEach((version) => {
    const dateStr = version.modified ?? version.created;
    const label = formatDateLabel(dateStr);
    const ts = new Date(dateStr).getTime();
    if (version.diff && version.diff.length > 0) {
      version.diff.forEach((entry, i) => {
        rows.push(
          buildDiffRows(
            entry,
            label,
            ts,
            `${version.version}-${i}`,
            userRoleMap
          )
        );
      });
    } else {
      rows.push({
        key: `${version.version}-created`,
        date: label,
        timestamp: ts,
        content: <span className="text-main text-xmd">Created</span>,
      });
    }
  });
  return rows;
};

/** Build history rows from transactions that have allocations pointing to this invoice. */
const buildAllocationRows = (
  transactions: Transaction[],
  invoiceId: string,
  userRoleMap: UserMap
): HistoryRow[] => {
  const rows: HistoryRow[] = [];
  transactions.forEach((txn) => {
    const matchingAllocations = txn.allocations.filter(
      (alloc) => alloc.invoice_id === invoiceId
    );
    matchingAllocations.forEach((alloc, i) => {
      const code = txn.currency as CurrencyCode;
      const precision = currencyPrecisionMap[code] || 2;
      const isPayin = alloc.type === 'invoice_payin';
      const dateStr = txn.modified ?? txn.posted;
      const label = formatDateLabel(dateStr);
      const ts = new Date(dateStr).getTime();
      const userExternalId = alloc.user.id;
      // Try to find role from externalId mapping (userRoleMap keys are externalIds)
      const role = userRoleMap.get(userExternalId);

      rows.push({
        key: `alloc-${txn.id}-${i}`,
        date: label,
        timestamp: ts + i,
        content: (
          <div className="text-main text-xmd flex flex-col gap-y-[2px]">
            <div className="flex items-center gap-x-[6px]">
              <span className="font-FragmentMono">{isPayin ? '↙' : '↗'}</span>
              <span className="text-main-500">
                {isPayin ? 'Payin' : 'Payout'}
              </span>
            </div>
            <span className="text-main-500">{txn.external_id}</span>
            {userExternalId && (
              <div className="flex items-center gap-x-[6px]">
                {role && <RoleColorDot role={role} size={10} hoverAnimate />}
                <span className="text-main-500">{userExternalId}</span>
              </div>
            )}
            <FormatBillingAmount
              amount={Math.abs(Number(alloc.amount))}
              currencyCode={code}
              currencyPrecision={precision}
            />
          </div>
        ),
      });
    });
  });
  return rows;
};

const InvoiceHistoryPanelContent = ({
  invoiceId,
  sortOrder,
  userRoleMap,
}: {
  invoiceId: string;
  sortOrder: 'newest' | 'oldest';
  userRoleMap: UserMap;
}) => {
  const { data } = useGetInvoicesIdHistorySuspense(invoiceId);
  const { data: txData } = useGetTransactions();

  const rows = useMemo(() => {
    const versionRows = flattenVersions([...data.data], userRoleMap);
    const allTransactions = (txData?.data ?? []) as Transaction[];
    const allocationRows = buildAllocationRows(
      allTransactions,
      invoiceId,
      userRoleMap
    );
    const allRows = [...versionRows, ...allocationRows];
    allRows.sort((a, b) =>
      sortOrder === 'newest'
        ? b.timestamp - a.timestamp
        : a.timestamp - b.timestamp
    );
    return allRows;
  }, [data.data, txData, invoiceId, userRoleMap, sortOrder]);

  return (
    <div className="flex flex-col overflow-y-auto flex-1">
      {rows.length === 0 ? (
        <div className="text-main-500 text-xmd px-[13.5px] py-f1">
          No history yet.
        </div>
      ) : (
        rows.map((row) => {
          const isPayment = row.key.startsWith('alloc-');
          return (
            <div
              key={row.key}
              className={clsx(
                'group flex flex-row gap-x-f1 px-[13.5px] py-f1 border-b-[0.5px] border-main-200 hover:bg-negative',
                isPayment && 'bg-main-100'
              )}
            >
              <div className="flex-1 min-w-0">{row.content}</div>
              <span className="text-main-500 text-xmd shrink-0">
                {row.date}
              </span>
            </div>
          );
        })
      )}
    </div>
  );
};

const SKELETON_MAX_ROWS = 6;

const HistoryLoadingSkeleton = () => {
  const [visibleRows, setVisibleRows] = useState(0);
  const directionRef = useRef(1);

  useEffect(() => {
    const timer = setInterval(() => {
      setVisibleRows((prev) => {
        const next = prev + directionRef.current;
        if (next >= SKELETON_MAX_ROWS) {
          directionRef.current = -1;
          return SKELETON_MAX_ROWS;
        }
        if (next <= 0) {
          directionRef.current = 1;
          return 0;
        }
        return next;
      });
    }, 150);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="flex flex-col flex-1">
      {/* eslint-disable react/no-array-index-key */}
      {Array.from({ length: visibleRows }).map((_, i) => (
        <div
          key={`skeleton-${i}`}
          className="px-[13.5px] py-f1 border-b-[0.5px] border-main-200 min-h-f4"
        />
      ))}
      {/* eslint-enable react/no-array-index-key */}
    </div>
  );
};

type InvoiceHistoryPanelProps = {
  invoiceId: string;
  userRoleMap: Map<string, string>;
};

export const InvoiceHistoryPanel = ({
  invoiceId,
  userRoleMap,
}: InvoiceHistoryPanelProps) => {
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');

  return (
    <div className="flex flex-col h-full">
      <div className="text-main text-xmd px-[13.5px] h-f4 flex items-center justify-between border-b-[0.5px] border-main-300">
        <span>History</span>
        <button
          type="button"
          onClick={() =>
            setSortOrder((prev) => (prev === 'newest' ? 'oldest' : 'newest'))
          }
          className="flex items-center gap-x-0.5 text-xmd text-main-500 hover:text-main cursor-pointer opacity-0 group-hover/history:opacity-100 transition-opacity"
        >
          Recent
          <span className="font-FragmentMono">
            {sortOrder === 'newest' ? '↓' : '↑'}
          </span>
        </button>
      </div>
      <Suspense fallback={<HistoryLoadingSkeleton />}>
        <InvoiceHistoryPanelContent
          invoiceId={invoiceId}
          sortOrder={sortOrder}
          userRoleMap={userRoleMap}
        />
      </Suspense>
    </div>
  );
};
